import { parseDate, parseTime, type DateValue, type Time } from '@internationalized/date';
import type { TimeValue } from '@react-types/datepicker';
import { format, parse, isValid, parseISO, isAfter, startOfDay } from 'date-fns';

/**
 * Конвертирует Date в DateValue для компонентов HeroUI
 * @param date - объект Date
 * @returns DateValue или null
 */
export function dateToDateValue(date: Date | null): DateValue | null {
  if (!date || !isValid(date)) return null;

  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateValue = parseDate(dateStr);

    // Проверяем, что объект DateValue создан правильно
    if (!dateValue || !dateValue.year || !dateValue.month || !dateValue.day) {
      return null;
    }

    return dateValue;
  } catch (error) {
    return null;
  }
}

/**
 * Конвертирует DateValue в Date
 * @param dateValue - объект DateValue
 * @returns Date или null
 */
export function dateValueToDate(dateValue: DateValue | null): Date | null {
  if (!dateValue) return null;

  try {
    const dateStr = `${dateValue.year}-${String(dateValue.month).padStart(2, '0')}-${String(dateValue.day).padStart(2, '0')}`;
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
}

/**
 * Конвертирует Date в Time для компонентов HeroUI
 * @param date - объект Date
 * @returns Time или null
 */
export function dateToTime(date: Date | null): Time | null {
  if (!date || !isValid(date)) return null;

  try {
    const timeStr = format(date, 'HH:mm');
    const time = parseTime(timeStr);

    // Проверяем, что объект Time создан правильно
    if (!time || time.hour === undefined || time.minute === undefined) {
      return null;
    }

    return time;
  } catch (error) {
    return null;
  }
}

/**
 * Конвертирует Time в Date (с сегодняшней датой)
 * @param time - объект Time
 * @param baseDate - базовая дата (по умолчанию сегодня)
 * @returns Date или null
 */
export function timeToDate(time: Time | null, baseDate: Date = new Date()): Date | null {
  if (!time) return null;

  try {
    const date = new Date(baseDate);
    date.setHours(time.hour, time.minute, 0, 0);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
}

/**
 * Парсит ISO строку в Date и разделяет на DateValue и Time
 * Автоматически конвертирует UTC время в локальное время браузера
 *
 * Пример: UTC "2025-10-21T23:30:00.000Z" → Локальное (UTC+3) 22.10.2025 02:30
 *
 * @param isoString - ISO строка (например, "2024-11-25T15:00:00")
 * @returns объект с dateValue и time в локальном времени браузера
 */
export function parseISOToDateValueAndTime(isoString: string | null): {
  dateValue: DateValue | null;
  time: Time | null;
} {
  if (!isoString) {
    return { dateValue: null, time: null };
  }

  try {
    let parsedDate: Date;

    // Пробуем разные способы парсинга
    if (isoString.includes('T')) {
      // ISO формат с T - парсим как UTC и получаем локальное время
      parsedDate = parseISO(isoString);
    } else if (isoString.includes(' ')) {
      // Формат с пробелом: "2024-01-01 15:30:00"
      parsedDate = parse(isoString, 'yyyy-MM-dd HH:mm:ss', new Date());
    } else if (isoString.includes(':')) {
      // Только время: "15:30:00"
      const today = new Date();
      const [hours, minutes, seconds] = isoString.split(':').map(Number);
      parsedDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        hours,
        minutes,
        seconds || 0,
      );
    } else {
      // Только дата: "2024-01-01"
      parsedDate = parse(isoString, 'yyyy-MM-dd', new Date());
    }

    if (!isValid(parsedDate)) {
      return { dateValue: null, time: null };
    }

    const dateValue = dateToDateValue(parsedDate);
    const time = dateToTime(parsedDate);

    return {
      dateValue,
      time,
    };
  } catch {
    return { dateValue: null, time: null };
  }
}

/**
 * Парсит ISO строку и возвращает DateValue и Time как в строке (UTC), без перевода в локальную зону.
 * Для отображения в форме: что в API (10:00Z) — то и показываем (10:00).
 */
export function parseISOToDateValueAndTimeAsStored(isoString: string | null): {
  dateValue: DateValue | null;
  time: Time | null;
} {
  if (!isoString) {
    return { dateValue: null, time: null };
  }

  try {
    const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) {
      return { dateValue: null, time: null };
    }
    const [, y, m, d, h, min] = match.map(Number);
    const dateValue = parseDate(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    const time = parseTime(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    return {
      dateValue: dateValue ?? null,
      time: time ?? null,
    };
  } catch {
    return { dateValue: null, time: null };
  }
}

/**
 * Собирает ISO строку, трактуя время как UTC (без перевода из локальной зоны).
 * Для Edit: отображаем 10:00 из API, при сохранении отправляем 10:00Z.
 */
export function combineDateAndTimeAsUTC(dateValue: DateValue | null, time: Time | null): string | null {
  if (!dateValue || !time) return null;
  const y = dateValue.year;
  const m = String(dateValue.month).padStart(2, '0');
  const d = String(dateValue.day).padStart(2, '0');
  const h = String(time.hour).padStart(2, '0');
  const min = String(time.minute).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}:00.000Z`;
}

/**
 * Объединяет DateValue и Time в строку формата ISO в UTC
 * Пользователь вводит локальное время, а мы конвертируем его в UTC для сервера
 *
 * Пример: Локальное (UTC+3) 22.10.2025 02:30 → UTC "2025-10-21T23:30:00.000Z"
 *
 * @param dateValue - объект DateValue с датой (локальное время)
 * @param time - объект Time с временем (локальное время)
 * @returns строка в формате ISO UTC (с Z на конце) или null если один из параметров отсутствует
 */
export function combineDateAndTime(dateValue: DateValue | null, time: Time | null): string | null {
  if (!dateValue || !time) return null;

  const date = dateValueToDate(dateValue);
  const timeDate = timeToDate(time, date || new Date());

  if (!date || !timeDate) return null;

  const combined = new Date(date);
  combined.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);

  return combined.toISOString();
}

/**
 * Форматирует DateValue в строку формата YYYY-MM-DD
 * @param dateObj - объект DateValue
 * @returns строка в формате YYYY-MM-DD
 */
export function formatDateToString(dateObj: DateValue): string {
  const { year, month, day } = dateObj;

  const paddedMonth = String(month).padStart(2, '0');
  const paddedDay = String(day).padStart(2, '0');

  return `${year}-${paddedMonth}-${paddedDay}`;
}

/**
 * Форматирует TimeValue в строку формата HH:mm:ss
 * @param time - объект TimeValue
 * @returns строка в формате HH:mm:ss
 */
export function formatTimeToString(time: TimeValue): string {
  const h = String(time.hour).padStart(2, '0');
  const m = String(time.minute).padStart(2, '0');
  const s = String(time.second ?? 0).padStart(2, '0');

  return `${h}:${m}:${s}`;
}

export const DISPLAY_DATE_FORMAT = 'dd.MM.yyyy';

export function formatDateDisplay(date: Date): string {
  return format(date, DISPLAY_DATE_FORMAT);
}

export function convertIsoToDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '-';

  try {
    const date = parseISO(isoDate);
    return format(date, DISPLAY_DATE_FORMAT);
  } catch {
    return '-';
  }
}

/**
 * Извлекает время из ISO строки и форматирует для отображения
 * Использует date-fns для парсинга и форматирования
 * @param isoDate - ISO строка даты
 * @returns строка в формате HH:mm или пустая строка, если даты нет или она невалидна
 */
export function convertIsoToTime(isoDate: string | null | undefined): string {
  if (!isoDate) return '';

  try {
    const date = parseISO(isoDate);
    return format(date, 'HH:mm');
  } catch {
    return '';
  }
}

/**
 * Валидирует DateValue и конвертирует в строку формата YYYY-MM-DD
 * Использует date-fns для валидации
 * Проверки:
 * - Дата валидная
 * - Дата в допустимом диапазоне (1900-2100)
 * - Дата не позже максимальной (если allowFuture = false)
 * - dateFrom не позже dateTo (если передан compareTo)
 * @param date - объект DateValue
 * @param options - опции валидации
 * @returns строка в формате YYYY-MM-DD или undefined если дата невалидна
 */
export function validateDateValue(
  date: DateValue | null | undefined,
  options?: {
    maxDate?: Date;
    compareTo?: DateValue | null;
    shouldBeBefore?: boolean;
    allowFuture?: boolean;
  },
): string | undefined {
  if (!date) return undefined;

  // Проверка корректности значений перед созданием Date
  if (
    date.year < 1900 ||
    date.year > 2100 ||
    date.month < 1 ||
    date.month > 12 ||
    date.day < 1 ||
    date.day > 31
  ) {
    return undefined;
  }

  const dateJs = new Date(date.year, date.month - 1, date.day);

  if (!isValid(dateJs)) return undefined;

  // Проверка максимальной даты только если не разрешены будущие даты
  if (!options?.allowFuture) {
    const today = startOfDay(new Date());
    const maxDate = options?.maxDate || today;
    if (isAfter(dateJs, maxDate)) return undefined;
  }

  // Проверка относительно другой даты (для dateFrom/dateTo)
  if (options?.compareTo) {
    const compareToJs = new Date(
      options.compareTo.year,
      options.compareTo.month - 1,
      options.compareTo.day,
    );

    if (options.shouldBeBefore && isAfter(dateJs, compareToJs)) {
      return undefined;
    }
  }

  return formatDateToString(date);
}
